import posthog from 'posthog-js'
import { type PublishablePlatformType } from '@backend/lib/db/schema/platform'

// Funnel events for third-party connections. Successes are tracked by the backend
// (`platform_authorized`, `connector_connected`); attempts and failures only exist here,
// because a user who cancels at the provider never reaches our API.

export type ConnectorProvider = 'github' | 'notion' | 'google_calendar' | 'google_drive'

export type ConnectFailReason =
  | 'cancelled'
  | 'provider_error'
  | 'state_mismatch'
  | 'missing_verifier'
  | 'missing_code'
  | 'authorize_failed'
  | 'no_accounts'

// Started events fire right before a full-page redirect to the provider, which would drop a
// batched event. A beacon survives the navigation.
const BEFORE_REDIRECT = { send_instantly: true, transport: 'sendBeacon' } as const

// What providers send back when the user backs out of the consent screen, as opposed to
// something actually going wrong. Facebook/Instagram add `error_reason=user_denied`.
const CANCEL_ERRORS = new Set(['access_denied', 'user_cancelled_login', 'user_cancelled_authorize'])

export const providerErrorReason = (
  error: string,
  errorReason?: string | null,
): ConnectFailReason =>
  CANCEL_ERRORS.has(error) || errorReason === 'user_denied' ? 'cancelled' : 'provider_error'

const errorText = (error: unknown): string | undefined => {
  const text =
    typeof error === 'string' ? error : error instanceof Error ? error.message : undefined
  return text?.slice(0, 200)
}

export const trackPlatformConnectStarted = (platform: PublishablePlatformType) => {
  posthog.capture('platform_connect_started', { platform }, BEFORE_REDIRECT)
}

export const trackPlatformConnectFailed = (
  platform: PublishablePlatformType,
  reason: ConnectFailReason,
  error?: unknown,
) => {
  posthog.capture('platform_connect_failed', { platform, reason, error: errorText(error) })
}

export const trackConnectorConnectStarted = (provider: ConnectorProvider, isReauth = false) => {
  posthog.capture('connector_connect_started', { provider, is_reauth: isReauth }, BEFORE_REDIRECT)
}

export const trackConnectorConnectFailed = (
  provider: ConnectorProvider,
  reason: ConnectFailReason,
  error?: unknown,
) => {
  posthog.capture('connector_connect_failed', { provider, reason, error: errorText(error) })
}
